import React from 'react';

import { LIMIAR_CRITICO_HORAS, LIMIAR_PARADO_HORAS } from '../home/home.util';

/**
 * Um veículo no Telão, desenhado como placa (at-130).
 *
 * Faixa na cor da classe com os dias na oficina, número grande, modelo e cor, um selo (parado,
 * liberado) e a trilha das etapas de serviço. A placa fica CLARA nos dois temas, como placa de
 * verdade — só a borda e a sombra acompanham o tema.
 *
 * É `<button>`: clicar abre o mesmo modal "Informações do atendimento" do kanban.
 */
export type EstadoNaTrilha = 'feita' | 'inacabada' | 'atual' | 'paralela' | 'prevista';

export interface PontoDaTrilha {
  esteiraId: string;
  sigla: string;
  nome: string;
  estado: EstadoNaTrilha;
}

export interface VeiculoDoTelao {
  id: string;
  esteiraId: string | null;
  placa: string;
  modelo: string;
  cor: string;
  classe: string;
  corDaClasse: { fundo: string; texto: string };
  dias: number | null;
  horasParado: number | null;
  liberacao: 'TOTAL' | 'PARCIAL' | null;
  /** `null` = régua não carregada (ou falhou): a trilha some, nunca vira "tudo previsto". */
  trilha: PontoDaTrilha[] | null;
}

const ROTULO_ESTADO: Record<EstadoNaTrilha, string> = {
  feita: 'realizada',
  inacabada: 'serviço inacabado',
  atual: 'etapa atual',
  paralela: 'em paralelo',
  prevista: 'prevista',
};

export function seloDoVeiculo(v: Pick<VeiculoDoTelao, 'horasParado' | 'liberacao'>): { classe: string; texto: string } | null {
  if (v.horasParado !== null && v.horasParado >= LIMIAR_CRITICO_HORAS) {
    return { classe: 'telao-selo--critico', texto: `${Math.floor(v.horasParado / 24)} d parado` };
  }
  if (v.horasParado !== null && v.horasParado >= LIMIAR_PARADO_HORAS) {
    return { classe: 'telao-selo--atencao', texto: `${Math.floor(v.horasParado / 24)} d parado` };
  }
  if (v.liberacao === 'TOTAL') return { classe: 'telao-selo--liberado', texto: 'Liberado' };
  if (v.liberacao === 'PARCIAL') return { classe: 'telao-selo--parcial', texto: 'Liberação parcial' };
  return null;
}

interface PlacaVeiculoProps {
  veiculo: VeiculoDoTelao;
  onClick: (id: string) => void;
}

const PlacaVeiculo: React.FC<PlacaVeiculoProps> = ({ veiculo: v, onClick }) => {
  const selo = seloDoVeiculo(v);
  const dias = v.dias === null ? 'dias não informados' : `${v.dias} d na oficina`;
  const descricao = [v.modelo, v.cor].filter(Boolean).join(' · ') || 'Modelo não informado';
  const rotulo = `${v.placa}, ${descricao}, ${v.classe}, ${dias}${selo ? `, ${selo.texto}` : ''}`;

  return (
    <button type="button" className="telao-placa" onClick={() => onClick(v.id)} aria-label={rotulo} title={rotulo}>
      <span className="telao-placa-faixa" style={{ background: v.corDaClasse.fundo, color: v.corDaClasse.texto }}>
        <span>{v.classe}</span>
        <span>{dias}</span>
      </span>
      <span className="telao-placa-meio">
        <span className="telao-placa-numero">{v.placa}</span>
        {selo && <span className={`telao-selo ${selo.classe}`}>{selo.texto}</span>}
      </span>
      <span className="telao-placa-modelo">{descricao}</span>
      {v.trilha && v.trilha.length > 0 && (
        <span className="telao-trilha">
          {v.trilha.map((p) => (
            <span
              key={p.esteiraId}
              className={`telao-trilha-ponto telao-trilha-ponto--${p.estado}`}
              title={`${p.nome}: ${ROTULO_ESTADO[p.estado]}`}
            >
              <span className="telao-trilha-marca" />
              <span className="telao-trilha-sigla">{p.sigla}</span>
            </span>
          ))}
        </span>
      )}
      {v.trilha && v.trilha.length === 0 && <span className="telao-trilha-vazia">Sem etapas de serviço planejadas</span>}
    </button>
  );
};

export default PlacaVeiculo;
